import { useEffect, useRef } from 'react';
import { Upload, Button, Image } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import galleryService from 'services/gallery';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  filterFilesWithinMaxUploadSize,
  isWithinMaxUploadSize,
} from 'helpers/uploadLimits';

function attachPreview(file) {
  if (!file?.originFileObj || file.thumbUrl) return file;
  const preview = URL.createObjectURL(file.originFileObj);
  return { ...file, preview, thumbUrl: preview };
}

function resolvePreviewUrl(file) {
  if (!file) return '';
  if (file.thumbUrl) return file.thumbUrl;
  if (file.preview) return file.preview;
  return '';
}

function revokePreview(file) {
  const url = file?.preview || file?.thumbUrl;
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

function normalizeSelectedFiles(fileList) {
  return fileList
    .filter((f) => f.status !== 'removed')
    .map((f) =>
      attachPreview({
        ...f,
        status: 'done',
        percent: 100,
      }),
    );
}

/** Normalize gallery upload API body (axios interceptor returns top-level `{ data: { path, ... } }`). */
function galleryUploadRecord(res) {
  if (!res || typeof res !== 'object') return null;
  const inner =
    res.data != null && typeof res.data === 'object' && !Array.isArray(res.data)
      ? res.data
      : res;
  return inner;
}

const MONGO_ID_HEX = /^[a-f0-9]{24}$/i;

const ImageGallery = ({
  fileList,
  setFileList,
  type,
  form,
  multiple,
  loadingBtn,
  setLoadingBtn,
  imageList,
  setImageList,
  handleClose,
  galleryBlogLink,
  onBlogCoverGallerySuccess,
}) => {
  const { t } = useTranslation();
  const imageListRef = useRef(imageList);
  imageListRef.current = imageList;

  useEffect(
    () => () => {
      imageListRef.current?.forEach(revokePreview);
    },
    [],
  );

  const createImage = (file) => {
    const record = galleryUploadRecord(file) ?? file;
    const path = record?.path ?? record?.url ?? record?.title ?? '';
    const uid =
      record?.id ??
      record?.uid ??
      path ??
      `img-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return {
      uid,
      name: path,
      status: 'done',
      url: path,
      created: true,
    };
  };

  const handleSave = () => {
    const rawFiles = imageList
      .map((item) => item?.originFileObj ?? item)
      .filter((file) => file instanceof File || file instanceof Blob);
    if (!rawFiles.length) {
      toast.warning(
        t('please.select.at.least.one.image') ||
          'Please select at least one image to upload.',
      );
      return;
    }
    const oversized = rawFiles.filter((file) => !isWithinMaxUploadSize(file));
    if (oversized.length) {
      toast.error(t('max.10.mb') || 'File size must be 10 MB or less');
      return;
    }
    if (!type || !String(type).trim()) {
      toast.warning(t('type.is.required') || 'Type is required.');
      return;
    }
    setLoadingBtn(true);
    const typeValue = String(type).trim();
    const isBlogsFolder = typeValue.toLowerCase() === 'blogs';
    const hasBlogLink =
      galleryBlogLink &&
      (Boolean(String(galleryBlogLink.blogId ?? '').trim()) ||
        Boolean(String(galleryBlogLink.blogUuid ?? '').trim()));
    let useBlogLinkOnUpload = isBlogsFolder && hasBlogLink && rawFiles.length === 1;
    if (isBlogsFolder && hasBlogLink && rawFiles.length > 1) {
      toast.warning(
        t('gallery.blog.link.single.file.only') ||
          'Blog cover link applies to one file only; uploading without blog_id.',
      );
    }

    const uploads = rawFiles.map((file) => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', typeValue);
      if (useBlogLinkOnUpload) {
        const bid = String(galleryBlogLink.blogId || '').trim();
        if (bid && MONGO_ID_HEX.test(bid)) {
          formData.append('blog_id', bid);
        }
        const buuid = String(galleryBlogLink.blogUuid || '').trim();
        if (buuid) {
          formData.append('blog_uuid', buuid);
        }
      }
      return galleryService.upload(formData);
    });
    Promise.all(uploads)
      .then((responses) => {
        const images = responses.map((res) => createImage(res));
        setFileList((prev) => [...prev, ...images]);
        // Optional: a picker whose value lives in component state rather than a
        // form field (the banner form's phone-artwork slot) passes no `form`,
        // and mirroring into an `images` field there would overwrite the
        // desktop picker's value.
        form?.setFieldsValue?.({
          images: [...(fileList ?? []), ...images],
        });
        if (useBlogLinkOnUpload && onBlogCoverGallerySuccess) {
          const rec = galleryUploadRecord(responses[0]);
          const path = rec?.path ?? rec?.url ?? '';
          if (path) onBlogCoverGallerySuccess({ path });
        }
        handleClose();
      })
      .catch((err) => {
        const msg = err?.response?.data?.message;
        toast.error(
          typeof msg === 'string' && msg.trim() ? msg : t('error') || 'Upload failed',
        );
      })
      .finally(() => setLoadingBtn(false));
  };

  const handleChange = (info) => {
    const filtered = filterFilesWithinMaxUploadSize(
      normalizeSelectedFiles(info.fileList),
      () => toast.error(t('max.10.mb') || 'File size must be 10 MB or less'),
    );
    setImageList(filtered);
  };

  const handleRemove = (file) => {
    revokePreview(file);
    setImageList((prev) => prev.filter((item) => item.uid !== file.uid));
  };

  const canAddMore = multiple || !imageList.length;

  return (
    <div className='upload-media-gallery upload-media-gallery--slim'>
      <p className='upload-media-gallery__hint'>
        {t('upload.image.hint') ||
          'Choose image(s) below, then click Save to upload.'}{' '}
        ({t('max.10.mb')})
      </p>

      <div className='upload-media-gallery__picker'>
        {imageList.map((item, index) => {
          const src = resolvePreviewUrl(item);
          if (!src) return null;
          return (
            <div
              className='upload-media-gallery__thumb'
              key={item?.uid ?? `sel-${index}`}
            >
              <Image src={src} alt='' className='upload-media-gallery__thumb-img' />
              <button
                type='button'
                className='upload-media-gallery__thumb-remove'
                aria-label={t('delete')}
                onClick={() => handleRemove(item)}
              >
                <DeleteOutlined />
              </button>
            </div>
          );
        })}

        {canAddMore && (
          <Upload
            accept='.png,.jpg,.jpeg,.webp,.avif,.jfif'
            showUploadList={false}
            beforeUpload={() => false}
            onChange={handleChange}
            fileList={imageList}
            multiple={multiple}
            className='upload-media-gallery__upload'
          >
            <div className='upload-media-gallery__trigger'>
              <PlusOutlined />
              <span>{t('select.image') || t('upload')}</span>
            </div>
          </Upload>
        )}
      </div>

      <div className='upload-media-gallery__footer'>
        <Button onClick={handleClose}>{t('cancel')}</Button>
        <Button
          type='primary'
          onClick={handleSave}
          loading={loadingBtn}
          disabled={!imageList?.length}
        >
          {t('save')}
        </Button>
      </div>
    </div>
  );
};

export default ImageGallery;
